import {
  DEFAULT_RIBBON_STYLE,
  HomeTileConfigStatus,
  IPersonaRuleNode,
  IRibbonColors,
} from "@/api_framework/api_modals/HomeTileConfig";
import {
  IClinicApiRow,
  IClinicOption,
  IClinicTagConfig,
  IClinicTagConfigSaveBody,
  IClinicTagConfigUpdateBody,
} from "@/api_framework/api_modals/ClinicTagConfig";
import { toWireStatus } from "@/app/hooks/api_hooks/quick_commerce/useHomeTileConfigAPI";
import {
  DEFAULT_BASIC_PERSONA_LOGIC,
  IPersonaLogicFormFields,
  RIBBON_TEXT_CONTENT_PATTERN,
  RIBBON_TEXT_MAX_LENGTH,
  RIBBON_TEXT_MIN_LENGTH,
  getRibbonTextLength,
} from "../HomeTileConfig/interface";
import { fromPersonaRule, validatePersonaCombination } from "../HomeTileConfig/personaExpression";
import { toBasicPersonaLogic } from "../HomeTileConfig/HomeTileOnboard/basicPersonaLogic";

export interface IClinicTagFormFields extends IPersonaLogicFormFields {
  chip_txt: string;
  ribbon: IRibbonColors;
  status: HomeTileConfigStatus;
}

export const DEFAULT_VALUES: IClinicTagFormFields = {
  persona_logic_mode: "BASIC",
  basic_persona: { ...DEFAULT_BASIC_PERSONA_LOGIC },
  persona_combination: "",
  chip_txt: "",
  ribbon: {
    bg_color: DEFAULT_RIBBON_STYLE.bg_color,
    text_color: DEFAULT_RIBBON_STYLE.text_color,
    border_color: DEFAULT_RIBBON_STYLE.border_color,
  },
  status: "ACTIVE",
};

export const toClinicOptions = (rows: IClinicApiRow[]): IClinicOption[] =>
  rows
    .map((row) => ({
      clinic_id: row.clinic_id ?? row.id ?? "",
      clinic_name: row.clinic_name ?? "",
    }))
    .filter((clinic) => Boolean(clinic.clinic_id));

export const TAG_TEXT_MAX_LENGTH = RIBBON_TEXT_MAX_LENGTH;

export const toRuleKey = (rule: IPersonaRuleNode | null): string =>
  rule ? (validatePersonaCombination(fromPersonaRule(rule)).canonical ?? "") : "";

export const validateTagText = (value: string): true | string => {
  const trimmed = value.trim();
  if (!trimmed) return "Tag text is required.";

  const length = getRibbonTextLength(trimmed);
  if (length < RIBBON_TEXT_MIN_LENGTH) return `Tag text must be at least ${RIBBON_TEXT_MIN_LENGTH} characters.`;
  if (length > RIBBON_TEXT_MAX_LENGTH) return `Tag text must be ${RIBBON_TEXT_MAX_LENGTH} characters or less.`;
  return RIBBON_TEXT_CONTENT_PATTERN.test(trimmed) || "Tag text must include at least one letter, number or emoji.";
};

export const buildSaveBody = (
  clinicId: string,
  data: IClinicTagFormFields,
  rule: IPersonaRuleNode,
): IClinicTagConfigSaveBody => ({
  clinic_id: clinicId,
  configuration: { persona_rule: rule },
  chip_txt: data.chip_txt.trim(),
  ribbon: { ...data.ribbon },
  status: toWireStatus(data.status),
});

export const buildChanges = (body: IClinicTagConfigSaveBody, saved: IClinicTagConfig): IClinicTagConfigUpdateBody => {
  const changes: IClinicTagConfigUpdateBody = {};

  if (body.chip_txt !== saved.chip_txt) changes.chip_txt = body.chip_txt;
  if (body.status !== toWireStatus(saved.status)) changes.status = body.status;
  if ((Object.keys(body.ribbon) as (keyof IRibbonColors)[]).some((key) => body.ribbon[key] !== saved.ribbon[key])) {
    changes.ribbon = body.ribbon;
  }
  if (toRuleKey(body.configuration.persona_rule ?? null) !== toRuleKey(saved.persona_rule)) {
    changes.configuration = body.configuration;
  }

  return changes;
};

export const toFormValues = (config: IClinicTagConfig): IClinicTagFormFields => {
  const basic = toBasicPersonaLogic(config.persona_rule);
  return {
    persona_logic_mode: basic ? "BASIC" : "ADVANCED",
    basic_persona: basic ?? { ...DEFAULT_BASIC_PERSONA_LOGIC },
    persona_combination: fromPersonaRule(config.persona_rule),
    chip_txt: config.chip_txt,
    ribbon: { ...config.ribbon },
    status: config.status,
  };
};
